import { z } from 'zod';
import { DateTime as D } from 'luxon';
import { WorkspaceTypes as WT } from 'shared/forecast/workspaceTypes';
import { useMemo } from 'react';
import { customAlphabet } from 'nanoid';
import { entries, sortBy } from 'remeda';
import { Workspace as W } from '../workspace';
import { dbFormSubset } from 'components/hooks/useDbForm';
import { NotNull } from 'shared/type';
import { uri } from 'shared/uri';
import { env } from 'utils/env';
import QRCode from 'utils/qrcode-svg';
import { doSafe } from 'shared/promise';
const newInviteCode = customAlphabet('123456789ABCDEFGHIJKLMNPQRSTUVWXYZ', 5);
export const useInviteLinks = ({
  uWorkspace,
  showExpired,
  path
}: {
  uWorkspace: W.UseAdminWorkspace;
  showExpired?: boolean;
  path?: string;
}) => {
  const {
    form: workspaceForm,
    id: workspace_id
  } = uWorkspace;
  const {
    setFieldValue,
    values
  } = dbFormSubset<W.Cfg['inviteLinks']>(workspaceForm as any, 'inviteLinks');
  const links = values && workspace_id ? getLinks({
    workspace_id,
    inviteLinks: values,
    showExpired,
    path
  }) : undefined;
  const createInviteLink = (data: z.infer<typeof WT.zInviteLinkNew>) => {
    if (!workspace_id) return;
    const id = newInviteCode();
    setFieldValue(id, {
      ...data,
      created: D.now().toISO(),
      join: data.expires != null
    });
    return id;
  };
  const updateInviteLink = (id: string, updates: Partial<WT.InviteLinkCfg>) => {
    setFieldValue(id, updates);
  };
  const disableInviteLink = (id: string) => {
    setFieldValue(id, {
      disabled: true
    });
  };
  const getInviteLinkForm = (id: string) => {
    return dbFormSubset<W.InviteLinkCfg>(workspaceForm, `inviteLinks.${id}`);
  };
  return {
    links,
    workspace_id,
    workspaceForm,
    getInviteLinkForm,
    createInviteLink,
    updateInviteLink,
    disableInviteLink,
    isDirty: workspaceForm.isDirty,
    isPending: uWorkspace.isPending,
    isError: uWorkspace.isError,
    error: uWorkspace.error
  };
};
export type UseInviteLinks = ReturnType<typeof useInviteLinks>;

/**
 * Valid invite links for the current workspace.
 * Uses the current page path to find matching invite links.
 */
export const useInviteLink = ({
  uWorkspace
}: {
  uWorkspace: W.UseAdminWorkspace;
}) => {
  const adminCfg = uWorkspace.row?.cfg;
  const path = typeof window !== 'undefined' ? uri(window.location.href).path?.join('/') ?? '' : '';
  return doSafe(getLinks({
    workspace_id: uWorkspace.id,
    inviteLinks: adminCfg?.inviteLinks ?? {},
    path
  }), l => l[0]);
};
// export type InviteLink = NotNull<ReturnType<typeof useInviteLink>>

const getLinks = ({
  workspace_id,
  inviteLinks,
  path,
  showExpired
}: {
  workspace_id: string;
  inviteLinks: W.Cfg['inviteLinks'];
  path?: string;
  showExpired?: boolean;
}) => sortBy(entries(inviteLinks).map(([k, v]) => ({
  ...v,
  id: k,
  url: inviteLinkUrl({
    workspace_id,
    invite: k,
    anon: v.anon,
    path: v.path,
    noPrompt: v.noPrompt
  })
})).filter(l => isLinkActive({
  l,
  showExpired,
  path
})), [s => s.created, 'desc']);
export type InviteLink = NotNull<ReturnType<typeof getLinks>>[number];
export const isLinkActive = ({
  l,
  showExpired,
  path
}: {
  l: Pick<WT.InviteLinkCfg, 'expires' | 'disabled' | 'path'>;
  showExpired?: boolean;
  path?: string;
}) => !l.disabled && (showExpired || !l.expires || D.fromISO(l.expires).diffNow().toMillis() > 0) && (path == null || l.path == path);
export const inviteLinkUrl = ({
  workspace_id,
  anon,
  invite,
  path,
  noPrompt
}: Pick<WT.InviteLinkCfg, 'noPrompt' | 'path' | 'anon'> & {
  invite: string;
  workspace_id: string;
}) => (path ? uri(env.URL).addPath(path) : uri(env.URL)).mergeQuery({
  workspace: workspace_id,
  invite,
  anon,
  noPrompt
}).url;
export const useQrImageUrl = (url: string, options?: {
  size?: number;
}) => useMemo(() => qrImageUrl(url, options), [url, JSON.stringify(options)]);
export const qrImageUrl = (url: string, options?: {
  size?: number;
}) => `data:image/svg+xml;base64,${btoa(new QRCode({
  content: url,
  width: options?.size ?? 400,
  height: options?.size ?? 400,
  color: '#000000',
  background: '#ffffff',
  padding: 1
}).svg())}`;